import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useCreateLink } from "@/features/links";
import type { LinkType, CreateLinkRequest } from "@/features/links";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

export function CreationHubPage() {
  const navigate = useNavigate();
  const createLink = useCreateLink();

  const [linkType, setLinkType] = useState<LinkType>("General");
  const [longUrl, setLongUrl] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("90");
  const [fallbackUrl, setFallbackUrl] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [isSingleUse, setIsSingleUse] = useState(false);
  const [generateQrCode, setGenerateQrCode] = useState(true);
  const [utmTerm, setUtmTerm] = useState("");
  const [utmContent, setUtmContent] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const utmParameters: Record<string, string> = {};
    if (utmSource) utmParameters.utm_source = utmSource;
    if (utmMedium) utmParameters.utm_medium = utmMedium;
    if (utmCampaign) utmParameters.utm_campaign = utmCampaign;
    if (utmTerm) utmParameters.utm_term = utmTerm;
    if (utmContent) utmParameters.utm_content = utmContent;

    const request: CreateLinkRequest = {
      linkType,
      longUrl,
      isSingleUse: isSingleUse || undefined,
      generateQrCode: generateQrCode ? undefined : false,
      customCode: linkType === "General" && customCode ? customCode : undefined,
      customerId: linkType === "Personal" ? customerId : undefined,
      expiresInDays: expiresInDays ? parseInt(expiresInDays) : undefined,
      fallbackUrl: fallbackUrl || undefined,
      utmParameters: Object.keys(utmParameters).length > 0 ? utmParameters : undefined,
    };

    try {
      const result = await createLink.mutateAsync(request);
      toast.success(`Link created: ${result.shortCode}`);
      navigate({ to: "/links/$shortCode", params: { shortCode: result.shortCode } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create link");
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Link</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Create a new short link</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Link Details</CardTitle>
          <CardDescription>Configure your new link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Link Type Toggle */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Link Type</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={linkType === "General" ? "default" : "outline"}
                  onClick={() => setLinkType("General")}
                >
                  General
                </Button>
                <Button
                  type="button"
                  variant={linkType === "Personal" ? "default" : "outline"}
                  onClick={() => setLinkType("Personal")}
                >
                  Personal
                </Button>
              </div>
            </div>

            {/* Single Use Toggle */}
            <div className="flex items-center gap-3">
              <input
                id="isSingleUse"
                type="checkbox"
                checked={isSingleUse}
                onChange={(e) => setIsSingleUse(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:text-blue-400 dark:focus:ring-blue-400"
              />
              <div>
                <label htmlFor="isSingleUse" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Single Use
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Link will be deleted after the first click</p>
              </div>
            </div>

            {/* Generate QR Code Toggle */}
            <div className="flex items-center gap-3">
              <input
                id="generateQrCode"
                type="checkbox"
                checked={generateQrCode}
                onChange={(e) => setGenerateQrCode(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:text-blue-400 dark:focus:ring-blue-400"
              />
              <div>
                <label htmlFor="generateQrCode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Generate QR Code
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Create a QR code image for this link</p>
              </div>
            </div>

            {/* Destination URL */}
            <div>
              <label htmlFor="longUrl" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Destination URL *
              </label>
              <Input
                id="longUrl"
                type="url"
                value={longUrl}
                onChange={(e) => setLongUrl(e.target.value)}
                placeholder="https://example.com/page"
                required
              />
            </div>

            {/* Custom Code (General only) */}
            {linkType === "General" && (
              <div>
                <label htmlFor="customCode" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Custom Short Code (optional)
                </label>
                <Input
                  id="customCode"
                  type="text"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  placeholder="my-custom-code"
                  pattern="^[a-zA-Z0-9_-]+$"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Letters, numbers, hyphens, and underscores only</p>
              </div>
            )}

            {/* Customer ID (Personal only) */}
            {linkType === "Personal" && (
              <div>
                <label htmlFor="customerId" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customer ID *
                </label>
                <Input
                  id="customerId"
                  type="text"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  placeholder="customer-123"
                  required
                />
              </div>
            )}

            {/* Expiration */}
            <div>
              <label htmlFor="expiresInDays" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Expires in (days)
              </label>
              <Input
                id="expiresInDays"
                type="number"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                min="1"
                max="365"
              />
            </div>

            {/* Fallback URL */}
            <div>
              <label htmlFor="fallbackUrl" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fallback URL (optional)
              </label>
              <Input
                id="fallbackUrl"
                type="url"
                value={fallbackUrl}
                onChange={(e) => setFallbackUrl(e.target.value)}
                placeholder="https://example.com/fallback"
              />
            </div>

            {/* UTM Parameters */}
            <div>
              <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">UTM Parameters (optional)</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="utmSource" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Source</label>
                  <Input id="utmSource" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="newsletter" />
                </div>
                <div>
                  <label htmlFor="utmMedium" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Medium</label>
                  <Input id="utmMedium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="email" />
                </div>
                <div>
                  <label htmlFor="utmCampaign" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Campaign</label>
                  <Input id="utmCampaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="summer-sale" />
                </div>
                <div>
                  <label htmlFor="utmTerm" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Term</label>
                  <Input id="utmTerm" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="keyword" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="utmContent" className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Content</label>
                  <Input id="utmContent" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="cta-button" />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={createLink.isPending}>
              {createLink.isPending ? "Creating..." : "Create Link"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
